import React, { useState } from 'react';
import theme from '../theme';

function GPSPointsModal({ showModal, gpsPoints, onClose, onAddPoint, onEditPoint, onDeletePoint }) {
  const [isAddMode, setIsAddMode] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    latitude: '',
    longitude: ''
  });

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      latitude: '',
      longitude: ''
    });
    setIsAddMode(false);
    setEditingId(null);
  };

  const handleStartAdd = () => {
    resetForm();
    setIsAddMode(true);
  };

  const handleStartEdit = (point) => {
    setFormData({
      name: point.name,
      description: point.description || '',
      latitude: point.lat.toFixed(7),
      longitude: point.lng.toFixed(7)
    });
    setEditingId(point.id);
    setIsAddMode(false);
  };

  const handleSave = () => {
    const lat = parseFloat(formData.latitude);
    const lng = parseFloat(formData.longitude);

    // Validate coordinates
    if (isNaN(lat) || isNaN(lng)) {
      alert('Please enter valid latitude and longitude values');
      return;
    }

    if (lat < -90 || lat > 90) {
      alert('Latitude must be between -90 and 90');
      return;
    }

    if (lng < -180 || lng > 180) {
      alert('Longitude must be between -180 and 180');
      return;
    }

    if (!formData.name.trim()) {
      alert('Please enter a name for the GPS point');
      return;
    }

    const pointData = {
      name: formData.name.trim(),
      description: formData.description.trim(),
      lat,
      lng
    };

    if (editingId) {
      onEditPoint(editingId, pointData);
    } else {
      onAddPoint(pointData);
    }

    resetForm();
  };

  const handleDelete = (pointId) => {
    if (window.confirm('Are you sure you want to delete this GPS point?')) {
      onDeletePoint(pointId);
      if (editingId === pointId) {
        resetForm();
      }
    }
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  if (!showModal) return null;

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0,0,0,0.5)',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1000
    }}>
      <div style={{
        backgroundColor: 'white',
        padding: '20px',
        borderRadius: '8px',
        maxWidth: '800px',
        width: '90%',
        maxHeight: '80vh',
        overflow: 'auto',
        boxShadow: '0 4px 6px rgba(0,0,0,0.1)'
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <h2 style={{ margin: 0, fontSize: '24px', color: '#333' }}>📍 GPS Points Manager</h2>
          <button
            onClick={handleClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: '24px',
              cursor: 'pointer',
              color: '#666'
            }}
          >
            ×
          </button>
        </div>

        {/* Add/Edit Form */}
        {(isAddMode || editingId) && (
          <div style={{
            backgroundColor: '#f9f9f9',
            padding: '15px',
            borderRadius: '5px',
            marginBottom: '20px'
          }}>
            <h3 style={{ marginTop: 0 }}>{editingId ? 'Edit GPS Point' : 'Add New GPS Point'}</h3>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Name *
                </label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="e.g., North Field Corner"
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Optional description"
                  rows={2}
                  style={{
                    width: '100%',
                    padding: '8px',
                    borderRadius: '4px',
                    border: '1px solid #ccc',
                    fontSize: '14px'
                  }}
                />
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Latitude * (-90 to 90)
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={formData.latitude}
                    onChange={(e) => setFormData({ ...formData, latitude: e.target.value })}
                    placeholder="59.329300"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px'
                    }}
                  />
                </div>
                <div style={{ flex: 1 }}>
                  <label style={{ display: 'block', marginBottom: '5px', fontWeight: 'bold' }}>
                    Longitude * (-180 to 180)
                  </label>
                  <input
                    type="number"
                    step="0.000001"
                    value={formData.longitude}
                    onChange={(e) => setFormData({ ...formData, longitude: e.target.value })}
                    placeholder="18.068600"
                    style={{
                      width: '100%',
                      padding: '8px',
                      borderRadius: '4px',
                      border: '1px solid #ccc',
                      fontSize: '14px'
                    }}
                  />
                </div>
              </div>
              <div style={{ display: 'flex', gap: '10px', marginTop: '10px' }}>
                <button
                  onClick={handleSave}
                  style={{
                    backgroundColor: theme.colors.success,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: 'bold'
                  }}
                >
                  {editingId ? 'Save Changes' : 'Add Point'}
                </button>
                <button
                  onClick={resetForm}
                  style={{
                    backgroundColor: theme.colors.mediumGrey,
                    color: 'white',
                    border: 'none',
                    borderRadius: '4px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '14px'
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Add Button */}
        {!isAddMode && !editingId && (
          <button
            onClick={handleStartAdd}
            style={{
              backgroundColor: theme.colors.primary,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: 'bold',
              marginBottom: '20px'
            }}
          >
            + Add New GPS Point
          </button>
        )}

        {/* GPS Points List */}
        <div>
          <h3 style={{ marginBottom: '15px' }}>
            Saved GPS Points ({gpsPoints.length})
          </h3>
          {gpsPoints.length === 0 ? (
            <p style={{ color: '#666', fontStyle: 'italic' }}>
              No GPS points saved yet. Click "Add New GPS Point" to create one.
            </p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {gpsPoints.map((point) => (
                <div
                  key={point.id}
                  style={{
                    backgroundColor: editingId === point.id ? theme.colors.primaryPale : theme.colors.paleGrey,
                    padding: '12px',
                    borderRadius: '5px',
                    border: editingId === point.id ? `2px solid ${theme.colors.warning}` : `1px solid ${theme.colors.lightGrey}`
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 'bold', fontSize: '16px', marginBottom: '5px', color: '#333' }}>
                        📍 {point.name}
                      </div>
                      {point.description && (
                        <div style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>
                          {point.description}
                        </div>
                      )}
                      <div style={{ fontSize: '13px', color: '#495057', fontFamily: 'monospace' }}>
                        <div>Lat: {point.lat.toFixed(7)}</div>
                        <div>Lng: {point.lng.toFixed(7)}</div>
                      </div>
                    </div>
                    <div style={{ display: 'flex', gap: '5px' }}>
                      <button
                        onClick={() => handleStartEdit(point)}
                        style={{
                          backgroundColor: theme.colors.info,
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '5px 10px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(point.id)}
                        style={{
                          backgroundColor: theme.colors.danger,
                          color: 'white',
                          border: 'none',
                          borderRadius: '3px',
                          padding: '5px 10px',
                          cursor: 'pointer',
                          fontSize: '12px'
                        }}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', paddingTop: '15px', borderTop: '1px solid #dee2e6' }}>
          <button
            onClick={handleClose}
            style={{
              backgroundColor: theme.colors.mediumGrey,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              padding: '10px 20px',
              cursor: 'pointer',
              fontSize: '14px',
              width: '100%'
            }}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}

export default GPSPointsModal;
